import { useEffect, useState } from 'react';
import { subscribeBookingRequestNotes } from '@/data/bookingRequestNotesRepo';
import { subscribeBookingRequests, subscribeBookingRequestsForPartner } from '@/data/bookingRequestsRepo';
import type { BookingRequestNotesType, BookingRequestType } from '@/types/types_bookingRequest';

/**
 * Subscribes to the whole booking-request queue, newest first. Admins only —
 * the rules refuse an unfiltered list to anyone else.
 *
 * `pendingCount` rides along because every caller wants it: the list badges its
 * heading with it, the dashboard raises it as a notification, and the sidebar
 * carries it on every admin page. Deriving it here from the same snapshot is
 * what keeps those three from ever disagreeing.
 */
export function useBookingRequests() {
  const [requests, setRequests] = useState<BookingRequestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeBookingRequests(
      (list) => {
        setRequests(list);
        setLoading(false);
        setError(null);
      },
      (e) => {
        console.error('Booking request subscription failed', e);
        setError('Could not load booking requests.');
        setLoading(false);
      },
    );
  }, []);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return { requests, pendingCount, loading, error };
}

/**
 * The desk's private notes, keyed by request id. Admins only, like the queue
 * itself — the rules refuse this collection to a partner outright.
 *
 * Its own hook rather than folded into `useBookingRequests`, because the notes
 * are wanted by exactly one screen and the pending count, the sidebar and the
 * dashboard all use that hook without ever reading a note.
 *
 * An empty map until the first snapshot lands, which reads as "no note" — right
 * for a queue that renders before it arrives, and harmless because the offer
 * form fills its box from the same map once it does.
 */
export function useBookingRequestNotes() {
  const [notes, setNotes] = useState<Map<string, BookingRequestNotesType>>(new Map());

  useEffect(() => {
    return subscribeBookingRequestNotes(setNotes, (e) => {
      console.error('Booking request notes subscription failed', e);
    });
  }, []);

  return notes;
}

/**
 * The same, for one partner's own requests. Used by the partner area, where the
 * unfiltered subscription above would be refused by the rules.
 *
 * Stays in the loading state when handed no partner, rather than reporting an
 * empty list: a signed-in user with no `partnerId` has not got zero requests,
 * we simply do not know whose to ask for. The caller shows that case for what
 * it is — see `PagePartnerRequests`.
 */
export function usePartnerBookingRequests(partnerId: string) {
  const [requests, setRequests] = useState<BookingRequestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!partnerId) return;
    return subscribeBookingRequestsForPartner(
      partnerId,
      (list) => {
        setRequests(list);
        setLoading(false);
        setError(null);
      },
      (e) => {
        console.error('Partner booking request subscription failed', e);
        setError('Could not load your booking requests.');
        setLoading(false);
      },
    );
  }, [partnerId]);

  return { requests, loading, error };
}
